import React, { ReactNode } from 'react'
import { Flex, Icon } from '..'
import { Mode } from '@ui/selects/LocationSelect'
import MFSelect from '@ui/selects/LocationSelect/MultiFloorSelect'
import MBSelect from '@ui/selects/LocationSelect/MultiBuildingSelect'
import MRCBSelect from '@ui/selects/LocationSelect/MultiRegionCityBuildingSelect'
import MultiSelect from './MultiSelect'
import { IBuildingDetails, IFloorDetails, IPortfolioRegion, IPortfolioCity } from '@api'
import css from './multiLocationSelect.module.scss'

interface MultiLocationSelectProps {
  mode: Mode
  values: any
  onChange: (item: any) => void
  includeOldBuildings?: boolean
}

export default function MultiLocationSelect({
  mode,
  values,
  onChange,
  includeOldBuildings,
}: MultiLocationSelectProps) {
  const buttonProps = {
    minimal: true,
    iconName: 'plus',
    blue: true,
    text: 'Add',
  }

  let tags: ReactNode[] = []
  let selectComponent: ReactNode

  switch (mode) {
    case Mode.MULTI_BUILDING:
      selectComponent = (
        <MBSelect
          includeOldBuildings={includeOldBuildings}
          values={values}
          onChange={(newValues) => {
            onChange(newValues)
          }}
          {...{ buttonProps }}
        />
      )

      tags = values.map((b: IBuildingDetails) => (
        <div key={b.id}>
          <div className={css.container_location_name}>{b.name}</div>
          <div className={css.container_city_name}>{`${b.city}${
            b.state ? `, ${b.state}` : ''
          }`}</div>
        </div>
      ))

      break

    case Mode.MULTI_FLOOR:
      selectComponent = (
        <MFSelect
          includeOldBuildings={includeOldBuildings}
          values={values}
          onChange={(newValues) => {
            onChange(newValues)
          }}
          {...{ buttonProps }}
        />
      )

      tags = values.map((f: IFloorDetails) => (
        <Flex key={f.id} direction="column">
          <div>
            <div className={css.container_location_name}>
              {f.name}
              <Icon name="long-arrow-right" style={{ margin: '0 4px' }} />
              {f.building?.name}
            </div>
            <div className={css.container_city_name}>{`${f?.building?.city}${
              f.building?.state ? `, ${f.building.state}` : ''
            }`}</div>
          </div>
        </Flex>
      ))

      break

    case Mode.MULTI_REGION_CITY_BUILDING:
      selectComponent = (
        <MRCBSelect
          includeOldBuildings={includeOldBuildings}
          mode={Mode.MULTI_REGION_CITY_BUILDING}
          values={values}
          onChange={(newValues) => {
            onChange(newValues)
          }}
          {...{ buttonProps }}
        />
      )

      tags = values.map((value: IPortfolioRegion | IPortfolioCity | IBuildingDetails) => {
        if ('cities' in value || 'buildings' in value) {
          // handle region/city
          return (
            <div key={value.name}>
              <div className={css.container_location_name}>{value.name}</div>
            </div>
          )
        } else if ('floors' in value) {
          // handle building
          return (
            <div key={value.id}>
              <div className={css.container_location_name}>{value.name}</div>
              <div className={css.container_city_name}>{`${value.city}${
                value.state ? `, ${value.state}` : ''
              }`}</div>
            </div>
          )
        }

        return null
      })

      break

    case Mode.MULTI_REGION_CITY:
      selectComponent = (
        <MRCBSelect
          mode={Mode.MULTI_REGION_CITY}
          values={values}
          onChange={(newValues) => {
            onChange(newValues)
          }}
          {...{ buttonProps }}
        />
      )

      tags = values.map((value: IPortfolioRegion | IPortfolioCity) => {
        if ('cities' in value || 'buildings' in value) {
          return (
            <div key={value.name}>
              <div className={css.container_location_name}>{value.name}</div>
            </div>
          )
        }
        return null
      })

      break

    default:
      throw new Error(`Unsupported mode - ${mode}`)
  }

  return (
    <MultiSelect
      title="Locations"
      values={tags}
      select={selectComponent}
      onRemove={(locationId: string) => {
        onChange(values.filter(({ id, name }: any) => id !== locationId && name !== locationId))
      }}
    />
  )
}
MultiLocationSelect.Mode = Mode
